// Media Resolver: robust evidence media URL resolution with caching
// Strategy:
// 1) casefiles/{casefileId}/evidence/{evidenceId}
// 2) evidence/{evidenceId}
// 3) Firebase Storage by storagePath -> getDownloadURL
// Returns: { url, mimeType, storagePath, source }

import { doc, getDoc } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { db, storage } from '../firebase';

const cache = new Map(); // key: casefileId|evidenceId -> { url, ts }
const TTL_MS = 5 * 60 * 1000; // 5 minutes

function makeKey({ evidenceId, casefileId }) {
  return `${casefileId || 'noCF'}|${evidenceId || 'noEV'}`;
}

export async function resolveEvidenceMedia({ evidenceId, casefileId }) {
  if (!evidenceId) throw new Error('resolveEvidenceMedia: missing evidenceId');
  const key = makeKey({ evidenceId, casefileId });
  const cached = cache.get(key);
  const now = Date.now();
  if (cached && (now - cached.ts) < TTL_MS) return cached.value;

  // Try nested under casefile
  let ev = null;
  try {
    if (casefileId) {
      const evDoc = await getDoc(doc(db, 'casefiles', casefileId, 'evidence', evidenceId));
      if (evDoc.exists()) ev = { id: evDoc.id, ...evDoc.data(), source: 'casefile' };
    }
  } catch (_) {}

  // Try top-level evidence
  if (!ev) {
    try {
      const evDoc2 = await getDoc(doc(db, 'evidence', evidenceId));
      if (evDoc2.exists()) ev = { id: evDoc2.id, ...evDoc2.data(), source: 'evidence' };
    } catch (_) {}
  }

  // Derive URL
  let url = null;
  let mimeType = ev?.contentType || ev?.mimeType || null;
  let storagePath = ev?.storagePath || ev?.filePath || null;

  if (ev?.downloadURL) url = ev.downloadURL;
  if (!url && storagePath) {
    try { url = await getDownloadURL(ref(storage, storagePath)); } catch (_) {}
  }

  if (!url) {
    const value = { url: null, mimeType, storagePath, source: ev?.source || 'unknown' };
    cache.set(key, { value, ts: now });
    return value;
  }

  const value = { url, mimeType, storagePath, source: ev?.source || 'unknown' };
  cache.set(key, { value, ts: now });
  return value;
}

export function invalidateEvidenceMediaCache({ evidenceId, casefileId }) {
  cache.delete(makeKey({ evidenceId, casefileId }));
}


